import type { ReactiveString, RequestOptions } from "../types/request";
import type { ServiceName } from "../types/service"

/**
 * @typedef {Object} listBrandingsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listBrandingsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addBrandingFormData Branding's data
 * @prop {string} organization_id - Organization Id
 * @prop {string|null=} favicon_img_id - Favicon Img Id
 * @prop {string|null=} logo_img_id - Logo Img Id
 * @prop {string|null=} profile_banner_img_id - Profile Banner Img Id
 */
export type addBrandingFormData = {
	organization_id: string;
	favicon_img_id?: string|null;
	logo_img_id?: string|null;
	profile_banner_img_id?: string|null;
};

/**
 * @typedef {Object} editBrandingFormData Branding's data
 * @prop {string=} organization_id - Organization Id
 * @prop {string|null=} favicon_img_id - Favicon Img Id
 * @prop {string|null=} logo_img_id - Logo Img Id
 * @prop {string|null=} profile_banner_img_id - Profile Banner Img Id
 */
export type editBrandingFormData = {
	organization_id?: string;
	favicon_img_id?: string|null;
	logo_img_id?: string|null;
	profile_banner_img_id?: string|null;
};

/**
 * @typedef {Object} addOnlineFormData Online's data
 * @prop {string} organization_id - Organization Id
 * @prop {string|null=} website - Website
 * @prop {string|null=} social_facebook - Social Facebook
 * @prop {string|null=} social_linkedin - Social Linkedin
 * @prop {string|null=} social_pinterest - Social Pinterest
 * @prop {string|null=} social_twitter - Social Twitter
 * @prop {string|null=} social_instagram - Social Instagram
 */
export type addOnlineFormData = {
	organization_id: string;
	website?: string|null;
	social_facebook?: string|null;
	social_linkedin?: string|null;
	social_pinterest?: string|null;
	social_twitter?: string|null;
	social_instagram?: string|null;
};

/**
 * @typedef {Object} editOnlineFormData Online's data
 * @prop {string=} organization_id - Organization Id
 * @prop {string|null=} website - Website
 * @prop {string|null=} social_facebook - Social Facebook
 * @prop {string|null=} social_linkedin - Social Linkedin
 * @prop {string|null=} social_pinterest - Social Pinterest
 * @prop {string|null=} social_twitter - Social Twitter
 * @prop {string|null=} social_instagram - Social Instagram
 */
export type editOnlineFormData = {
	organization_id?: string;
	website?: string|null;
	social_facebook?: string|null;
	social_linkedin?: string|null;
	social_pinterest?: string|null;
	social_twitter?: string|null;
	social_instagram?: string|null;
};

/**
 * @typedef {Object} addOrganizationFormData
 * @prop {Number=} tengiva_organization_code - Auto incremental field.
 * @prop {Number=} swatches_sourcing - Indicate if Tengiva manage the samples (swatches) or if the new organization source independantly.
 * @prop {string|null=} regionalsetting_id - Organization wide default regional settings.
 * @prop {string=} subsidiary_of - For organization under holding organization, the holding organization_id would be indicated in this field.
 * @prop {Number=} holding - Indicate if the new organization in the making is an holding organization.
 * @prop {Number=} status - Status determine if the organization should be listed or not
 * @prop {{}} profiles - Profiles fields pertinent to the new organization in the making.
 */
export type addOrganizationFormData = {
	tengiva_organization_code?: Number;
	swatches_sourcing?: Number;
	regionalsetting_id?: string|null;
	subsidiary_of?: string;
	holding?: Number;
	status?: Number;
	profiles: {};
};

/**
 * @typedef {Object} listAllOrganizationFormData Pagination parameters (can be provided via query parameters for GET requests).
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listAllOrganizationFormData = {
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} editOrganizationFormData
 * @prop {Number=} tengiva_organization_code - Auto incremental field.
 * @prop {Number=} swatches_sourcing - Indicate if Tengiva manage the samples (swatches) or if the new organization source independantly.
 * @prop {string=} regionalsetting_id - Organization wide default regional settings.
 * @prop {string=} subsidiary_of - For organization under holding organization, the holding organization_id would be indicated in this field.
 * @prop {Number=} holding - Indicate if the new organization in the making is an holding organization.
 * @prop {Number=} status - Status determine if the organization should be listed or not
 * @prop {{}=} profile - Profiles fields pertinent to the new organization in the making.
 */
export type editOrganizationFormData = {
	tengiva_organization_code?: Number;
	swatches_sourcing?: Number;
	regionalsetting_id?: string;
	subsidiary_of?: string;
	holding?: Number;
	status?: Number;
	profile?: {};
};

/**
 * @typedef {Object} viewOrganizationfromNameOrganizationFormData Organization's data
 * @prop {string} organization - Organization's profile's public name
 */
export type viewOrganizationfromNameOrganizationFormData = {
	organization: string;
};

/**
 * @typedef {Object} setHoldingOrganizationFormData Modify holding field of an organization
 * @prop {Number} holding - Set holding to true or false
 */
export type setHoldingOrganizationFormData = {
	holding: Number;
};

/**
 * @typedef {Object} addSubsidiaryOrganizationFormData Organization data
 * @prop {string} organization_id - Organization ID
 */
export type addSubsidiaryOrganizationFormData = {
	organization_id: string;
};

/**
 * @typedef {Object} listProfilesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listProfilesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addProfileFormData Profile's data
 * @prop {string} organization_id - Organization Id
 * @prop {string} public_name - Public Name
 * @prop {string=} slug - Slug
 * @prop {string|null=} description - Description
 * @prop {string|null=} country - Country
 */
export type addProfileFormData = {
	organization_id: string;
	public_name: string;
	slug?: string;
	description?: string|null;
	country?: string|null;
};

/**
 * @typedef {Object} editProfileFormData Profile's data
 * @prop {string=} organization_id - Organization Id
 * @prop {string=} public_name - Public Name
 * @prop {string=} slug - Slug
 * @prop {string|null=} description - Description
 * @prop {string|null=} country - Country
 */
export type editProfileFormData = {
	organization_id?: string;
	public_name?: string;
	slug?: string;
	description?: string|null;
	country?: string|null;
};

/**
 * @typedef {Object} listServiceSubscriptionsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listServiceSubscriptionsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} listAllServiceSubscriptionFormData Pagination parameters (can be provided via query parameters for GET requests).
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listAllServiceSubscriptionFormData = {
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} viewAllServiceSubscriptionFormData Pagination parameters (can be provided via query parameters for GET requests).
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type viewAllServiceSubscriptionFormData = {
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addWithPackagesServiceSubscriptionFormData Service Subscription's data
 * @prop {string} organization_id - Organization Id
 * @prop {string} service_id - Service Id
 * @prop {Number|null=} tier - Tier
 * @prop {Number|null=} onboarding_status - Onboarding Status
 * @prop {Number} status - Status (0, 1, 99)
 * @prop {string} subscription_duration - Some information about what it does
 * @prop {{}} subscription_packages - Some information about what it does
 * @prop {{}} subscription_platforms - Some information about what it does
 */
export type addWithPackagesServiceSubscriptionFormData = {
	organization_id: string;
	service_id: string;
	tier?: Number|null;
	onboarding_status?: Number|null;
	status: Number;
	subscription_duration: string;
	subscription_packages: {};
	subscription_platforms: {};
};

/**
 * @typedef {Object} addServiceSubscriptionFormData Service Subscription's data
 * @prop {string} organization_id - Organization Id
 * @prop {string} service_id - Service Id
 * @prop {Number|null=} tier - Tier
 * @prop {Number|null=} onboarding_status - Onboarding Status
 * @prop {Number} status - Status (0, 1, 99)
 * @prop {string=} subscription_duration - Id of the organizationin in the making.
 * @prop {{}=} subscription_packages - subscription_packages
 */
export type addServiceSubscriptionFormData = {
	organization_id: string;
	service_id: string;
	tier?: Number|null;
	onboarding_status?: Number|null;
	status: Number;
	subscription_duration?: string;
	subscription_packages?: {};
};

/**
 * @typedef {Object} editServiceSubscriptionFormData Service Subscription's data
 * @prop {string=} organization_id - Organization Id
 * @prop {string=} service_id - Service Id
 * @prop {Number|null=} tier - Tier
 * @prop {Number|null=} onboarding_status - Onboarding Status
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editServiceSubscriptionFormData = {
	organization_id?: string;
	service_id?: string;
	tier?: Number|null;
	onboarding_status?: Number|null;
	status?: Number;
};

/**
 * @typedef {Object} listSubscriptionPackagesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listSubscriptionPackagesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addSubscriptionPackageFormData Subscription Package's data
 * @prop {string} service_subscription_id - Service Subscription Id
 * @prop {string} package_id - Package Id
 * @prop {Number} status - Status (0, 1, 99)
 * @prop {string} expiration_date - Expiration Date
 */
export type addSubscriptionPackageFormData = {
	service_subscription_id: string;
	package_id: string;
	status: Number;
	expiration_date: string;
};

/**
 * @typedef {Object} editSubscriptionPackageFormData Subscription Package's data
 * @prop {string=} service_subscription_id - Service Subscription Id
 * @prop {string=} package_id - Package Id
 * @prop {Number=} status - Status (0, 1, 99)
 * @prop {string=} expiration_date - Expiration Date
 */
export type editSubscriptionPackageFormData = {
	service_subscription_id?: string;
	package_id?: string;
	status?: Number;
	expiration_date?: string;
};

/**
 * @typedef {Object} listSubscriptionPlatformsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listSubscriptionPlatformsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addSubscriptionPlatformFormData Subscription Platform's data
 * @prop {string} service_subscription_id - Service Subscription Id
 * @prop {string} variant - Variant
 * @prop {string} platform_id - Platform Id
 * @prop {Number} status - Status (0, 1, 99)
 */
export type addSubscriptionPlatformFormData = {
	service_subscription_id: string;
	variant: string;
	platform_id: string;
	status: Number;
};

/**
 * @typedef {Object} editSubscriptionPlatformFormData Subscription Platform's data
 * @prop {string=} service_subscription_id - Service Subscription Id
 * @prop {string=} variant - Variant
 * @prop {string=} platform_id - Platform Id
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editSubscriptionPlatformFormData = {
	service_subscription_id?: string;
	variant?: string;
	platform_id?: string;
	status?: Number;
};

/**
 * @typedef {Object} addSubscriptionFormData
 * @prop {Number=} tengiva_organization_code - Auto incremental field.
 * @prop {Number=} swatches_sourcing - Indicate if Tengiva manage the samples (swatches) or if the new organization source independantly.
 * @prop {string|null=} regionalsetting_id - Organization wide default regional settings.
 * @prop {string=} subsidiary_of - For organization under holding organization, the holding organization_id would be indicated in this field.
 * @prop {Number=} holding - Indicate if the new organization in the making is an holding organization.
 * @prop {Number=} status - Status determine if the organization should be listed or not
 * @prop {string=} service_id - Service id of which the new organization is subscribing to
 * @prop {{}} profile - Profiles fields pertinent to the new organization in the making.
 * @prop {{}=} branding - Branding fields pertinent to the new organization in the making.
 * @prop {{}=} online - Onlines fields pertinent to the new organization in the making.
 */
export type addSubscriptionFormData = {
	tengiva_organization_code?: Number;
	swatches_sourcing?: Number;
	regionalsetting_id?: string|null;
	subsidiary_of?: string;
	holding?: Number;
	status?: Number;
	service_id?: string;
	profile: {};
	branding?: {};
	online?: {};
};

/**
 * @typedef {Object} createOrganizationSubscriptionSubscriptionFormData
 * @prop {string} platform_id - Platform id where user is signing up to.
 * @prop {string} organization_legal_name - Organization legal name as given in form.
 * @prop {string} country - Organization country as given in form.
 * @prop {{}} user - User fields pertinent to the new User in the making.
 */
export type createOrganizationSubscriptionSubscriptionFormData = {
	platform_id: string;
	organization_legal_name: string;
	country: string;
	user: {};
};

/**
 * @typedef {Object} createOrganizationSubscriptionMinimalSubscriptionFormData
 * @prop {string} organization_legal_name - Organization legal name as given in form.
 * @prop {string} country - Organization country as given in form.
 * @prop {string=} business_number - Organization business number.
 * @prop {string|null=} package_id - ESM Package ID. Required for Merchant sign ups.
 * @prop {string|null=} platform_id - Platform ID. Required for Partner sign ups.
 * @prop {{}} user - User fields pertinent to the new organization in the making.
 */
export type createOrganizationSubscriptionMinimalSubscriptionFormData = {
	organization_legal_name: string;
	country: string;
	business_number?: string;
	package_id?: string|null;
	platform_id?: string|null;
	user: {};
};

const service = {
	name: "OrgM" as ServiceName,
	version: 1
};

export const useOrgMApi = () => {
	/**
	 * Call an endpoint on the OrgM
	 *
	 * Sets the service to the Request and forwards it to the TengivaApi.call() function
	 * @param {ReactiveString} endpoint - Endpoint to request
	 * @param {RequestOptions} options - Additonal options for the Request
	 * @returns {Promise<any>}
	 */
	const call = async <T = any>(endpoint: ReactiveString, options: RequestOptions) => {
		options = {
			...options,
			service
		};
		return await useTengivaApi<T>(endpoint, options);
	};

	/**
	 * Index method
	 * 
	 * @param {listBrandingsFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listBrandings = async <T = any>(formData: listBrandingsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/organizations_branding/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} branding - Branding id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewBranding = async <T = any>(branding: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organizations_branding/view`,
			[
				branding
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get the Branding for an Organization ID
	 * 
	 * Supports using the field_list query params:
	 * "?field_list=id,email,first_name"
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForOrganizationBranding = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organizations_branding/viewForOrganization`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addBrandingFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addBranding = async <T = any>(formData: addBrandingFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/organizations_branding/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} branding - Branding id.
	 * @param {editBrandingFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editBranding = async <T = any>(branding: ReactiveString, formData?: editBrandingFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organizations_branding/edit`,
			[
				branding
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} branding - Branding id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteBranding = async <T = any>(branding: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organizations_branding/delete`,
			[
				branding
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View Organization  details for a platform by ID
	 * 
	 * @param {ReactiveString} organizationId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewMerchantProfileMarketplace = async <T = any>(organizationId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/viewMerchantProfile`,
			[
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} online - Online id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewOnline = async <T = any>(online: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/onlines/view`,
			[
				online
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get the Online for an Organization ID
	 * 
	 * Supports using the field_list query params:
	 * "?field_list=id,email,first_name"
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewByOrganizationIdOnline = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/onlines/viewByOrganizationId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addOnlineFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addOnline = async <T = any>(formData: addOnlineFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/onlines/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} online - Online id.
	 * @param {editOnlineFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editOnline = async <T = any>(online: ReactiveString, formData?: editOnlineFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/onlines/edit`,
			[
				online
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} online - Online id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteOnline = async <T = any>(online: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/onlines/delete`,
			[
				online
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a new Organization
	 * 
	 * @param {addOrganizationFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addOrganization = async <T = any>(formData: addOrganizationFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/organizations/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * listAll method
	 * 
	 * @param {listAllOrganizationFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listAllOrganization = async <T = any>(formData?: listAllOrganizationFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/organizations/listAll`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View Organization by ID
	 * 
	 * @param {ReactiveString} organization - Resolved automatically from the UUID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewOrganization = async <T = any>(organization: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organizations/view`,
			[
				organization
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View Organization  details for a platform by ID
	 * 
	 * @param {ReactiveString} organizationId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewOrganizationFullDetailsOrganization = async <T = any>(organizationId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organizations/viewOrganizationFullDetails`,
			[
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View Organization by ID
	 * 
	 * @param {ReactiveString} organizationId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewOrganizationSummaryOrganization = async <T = any>(organizationId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/organizations/viewOrganizationSummary`,
			[
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit an Organization's data
	 * 
	 * @param {ReactiveString} organization - Resolved automatically from the UUID
	 * @param {editOrganizationFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editOrganization = async <T = any>(organization: ReactiveString, formData: editOrganizationFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organizations/edit`,
			[
				organization
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete an Organization by ID
	 * 
	 * @param {ReactiveString} organization - Resolved automatically from the UUID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteOrganization = async <T = any>(organization: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organizations/delete`,
			[
				organization
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get the public name of the organization
	 * 
	 * @param {ReactiveString} profile
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const idToNameOrganization = async <T = any>(profile: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/organizations/idToName`,
			[
				profile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * convertIdsToNames function
	 * 
	 * @param {ReactiveString} test
	 * @param {{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const convertIdsToNamesOrganization = async <T = any>(test: ReactiveString, formData: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/organizations/convertIdsToNames`,
			[
				test
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get the organization from name
	 * 
	 * @param {viewOrganizationfromNameOrganizationFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewOrganizationfromNameOrganization = async <T = any>(formData: viewOrganizationfromNameOrganizationFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/organizations/viewOrganizationfromName`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Sets the Organization as a holding company
	 * 
	 * @param {ReactiveString} organization
	 * @param {setHoldingOrganizationFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const setHoldingOrganization = async <T = any>(organization: ReactiveString, formData: setHoldingOrganizationFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organizations/setHolding`,
			[
				organization
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get if an organization is a holding company
	 * 
	 * @param {ReactiveString} organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const isHoldingOrganization = async <T = any>(organization: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organizations/isHolding`,
			[
				organization
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add a subsidiary for an organization
	 * organization_id should be a holding company
	 * 
	 * @param {ReactiveString} organization
	 * @param {addSubsidiaryOrganizationFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addSubsidiaryOrganization = async <T = any>(organization: ReactiveString, formData: addSubsidiaryOrganizationFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organizations/addSubsidiary`,
			[
				organization
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List subsidiaries of an organization
	 * 
	 * @param {ReactiveString} organizationId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listSubsidiariesOrganization = async <T = any>(organizationId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organizations/listSubsidiaries`,
			[
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * getListingForOrganizationIds function - List all merchants by their organization_id for a marketplace
	 * Will retrieve merchant's public_name, description and online.website in case subscription_platform variant is Showroom-Merchant.
	 * 
	 * @param {{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getListingForOrganizationIdsOrganization = async <T = any>(formData: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/organizations/getListingForOrganizationIds`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * listOrganizations function - List all merchants
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listOrganizationsOrganization = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/organizations/listOrganizations`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View Organization  details for a platform by ID
	 * 
	 * @param {ReactiveString} organizationId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPartnerProfilePartner = async <T = any>(organizationId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/partners/viewPartnerProfile`,
			[
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listProfilesFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listProfiles = async <T = any>(formData: listProfilesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/profiles/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} profile - Profile id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewProfile = async <T = any>(profile: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/profiles/view`,
			[
				profile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get the Profile for an Organization ID
	 * 
	 * Supports using the field_list query params:
	 * "?field_list=id,email,first_name"
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewByOrganizationIdProfile = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/profiles/viewByOrganizationId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addProfileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addProfile = async <T = any>(formData: addProfileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/profiles/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} profile - Profile id.
	 * @param {editProfileFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editProfile = async <T = any>(profile: ReactiveString, formData?: editProfileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/profiles/edit`,
			[
				profile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} profile - Profile id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteProfile = async <T = any>(profile: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/profiles/delete`,
			[
				profile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View Showroom Organization by ID or slug
	 * 
	 * @param {ReactiveString} profile - Resolved automatically from the UUID the slug
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewShowroomOrganizationProfile = async <T = any>(profile: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/profiles/viewShowroomOrganization`,
			[
				profile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listServiceSubscriptionsFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listServiceSubscriptions = async <T = any>(formData: listServiceSubscriptionsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/service_subscriptions/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * listAll method
	 * 
	 * @param {listAllServiceSubscriptionFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listAllServiceSubscription = async <T = any>(formData?: listAllServiceSubscriptionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/service_subscriptions/listAll`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewAll method
	 * 
	 * @param {viewAllServiceSubscriptionFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllServiceSubscription = async <T = any>(formData?: viewAllServiceSubscriptionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/service_subscriptions/viewAll`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} serviceSubscription - Service Subscription id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewServiceSubscription = async <T = any>(serviceSubscription: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/service_subscriptions/view`,
			[
				serviceSubscription
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all Organizations subscribed to a Service ID
	 * 
	 * @param {ReactiveString} service_id - Service
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllByServiceIdServiceSubscription = async <T = any>(service_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/service_subscriptions/viewAllByServiceId`,
			[
				service_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all Service Subscription matching an Organization ID
	 * 
	 * Supports all the paginate query params:
	 * "?field_list=id,email,first_name&order[field_name]=DIRECTION&limit=10&page=3"
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllByOrganizationIdServiceSubscription = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/service_subscriptions/viewAllByOrganizationId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * addWithPackages method
	 * 
	 * @param {addWithPackagesServiceSubscriptionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addWithPackagesServiceSubscription = async <T = any>(formData: addWithPackagesServiceSubscriptionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/service_subscriptions/addWithPackages`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addServiceSubscriptionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addServiceSubscription = async <T = any>(formData: addServiceSubscriptionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/service_subscriptions/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} serviceSubscription - Service Subscription id.
	 * @param {editServiceSubscriptionFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editServiceSubscription = async <T = any>(serviceSubscription: ReactiveString, formData?: editServiceSubscriptionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/service_subscriptions/edit`,
			[
				serviceSubscription
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} serviceSubscription - Service Subscription id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteServiceSubscription = async <T = any>(serviceSubscription: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/service_subscriptions/delete`,
			[
				serviceSubscription
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listSubscriptionPackagesFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listSubscriptionPackages = async <T = any>(formData?: listSubscriptionPackagesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/subscription_packages/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} subscriptionPackage - Subscription Package id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewSubscriptionPackage = async <T = any>(subscriptionPackage: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/subscription_packages/view`,
			[
				subscriptionPackage
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addSubscriptionPackageFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addSubscriptionPackage = async <T = any>(formData: addSubscriptionPackageFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/subscription_packages/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} subscriptionPackage - Subscription Package id.
	 * @param {editSubscriptionPackageFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editSubscriptionPackage = async <T = any>(subscriptionPackage: ReactiveString, formData?: editSubscriptionPackageFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/subscription_packages/edit`,
			[
				subscriptionPackage
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} subscriptionPackage - Subscription Package id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteSubscriptionPackage = async <T = any>(subscriptionPackage: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/subscription_packages/delete`,
			[
				subscriptionPackage
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listSubscriptionPlatformsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listSubscriptionPlatforms = async <T = any>(formData?: listSubscriptionPlatformsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/subscription_platforms/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} subscriptionPlatform - Subscription Platform id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewSubscriptionPlatform = async <T = any>(subscriptionPlatform: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/subscription_platforms/view`,
			[
				subscriptionPlatform
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addSubscriptionPlatformFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addSubscriptionPlatform = async <T = any>(formData: addSubscriptionPlatformFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/subscription_platforms/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} subscriptionPlatform - Subscription Platform id.
	 * @param {editSubscriptionPlatformFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editSubscriptionPlatform = async <T = any>(subscriptionPlatform: ReactiveString, formData?: editSubscriptionPlatformFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/subscription_platforms/edit`,
			[
				subscriptionPlatform
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} subscriptionPlatform - Subscription Platform id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteSubscriptionPlatform = async <T = any>(subscriptionPlatform: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/subscription_platforms/delete`,
			[
				subscriptionPlatform
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a new Organization and all its defaults
	 * 
	 * @param {addSubscriptionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addSubscription = async <T = any>(formData: addSubscriptionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/subscriptions/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a Buyer Organization and all its defaults (No auth requirement)
	 * 
	 * @param {createOrganizationSubscriptionSubscriptionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const createOrganizationSubscriptionSubscription = async <T = any>(formData: createOrganizationSubscriptionSubscriptionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/subscriptions/createOrganizationSubscription`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a Merchant or Partner Organization and all its defaults (No auth requirement)
	 * 
	 * @param {createOrganizationSubscriptionMinimalSubscriptionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const createOrganizationSubscriptionMinimalSubscription = async <T = any>(formData: createOrganizationSubscriptionMinimalSubscriptionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/subscriptions/createOrganizationSubscriptionMinimal`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};


	return {
		call,
		listBrandings,
		viewBranding,
		viewForOrganizationBranding,
		addBranding,
		editBranding,
		deleteBranding,
		viewMerchantProfileMarketplace,
		viewOnline,
		viewByOrganizationIdOnline,
		addOnline,
		editOnline,
		deleteOnline,
		addOrganization,
		listAllOrganization,
		viewOrganization,
		viewOrganizationFullDetailsOrganization,
		viewOrganizationSummaryOrganization,
		editOrganization,
		deleteOrganization,
		idToNameOrganization,
		convertIdsToNamesOrganization,
		viewOrganizationfromNameOrganization,
		setHoldingOrganization,
		isHoldingOrganization,
		addSubsidiaryOrganization,
		listSubsidiariesOrganization,
		getListingForOrganizationIdsOrganization,
		listOrganizationsOrganization,
		viewPartnerProfilePartner,
		listProfiles,
		viewProfile,
		viewByOrganizationIdProfile,
		addProfile,
		editProfile,
		deleteProfile,
		viewShowroomOrganizationProfile,
		listServiceSubscriptions,
		listAllServiceSubscription,
		viewAllServiceSubscription,
		viewServiceSubscription,
		viewAllByServiceIdServiceSubscription,
		viewAllByOrganizationIdServiceSubscription,
		addWithPackagesServiceSubscription,
		addServiceSubscription,
		editServiceSubscription,
		deleteServiceSubscription,
		listSubscriptionPackages,
		viewSubscriptionPackage,
		addSubscriptionPackage,
		editSubscriptionPackage,
		deleteSubscriptionPackage,
		listSubscriptionPlatforms,
		viewSubscriptionPlatform,
		addSubscriptionPlatform,
		editSubscriptionPlatform,
		deleteSubscriptionPlatform,
		addSubscription,
		createOrganizationSubscriptionSubscription,
		createOrganizationSubscriptionMinimalSubscription,
	}
};